import { directoryClient } from 'shared/domain/directory/directoryClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { fileToCreateDocumentationModel } from 'presentation/documentation/fileToCreateDocumentationModel';
import { userWithPermissionsInDtoToUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { UserWithPersonalData } from 'shared/domain/user/types/model';
import {
  DirectoriesBatchCreateModel,
  DirectoriesBatchCreatedModel,
} from 'shared/domain/directory/directoryModel';
import { showErrorToastWithMessage, showGenericErrorToast } from 'services/toaster';
import { logError } from 'setup/logger/logError';
import {
  FileReadError,
  FileReadSuccess,
  FileReadResult,
  ResultType,
  FILE_STATE_KIND,
} from 'components/common/graphicUploader/types';
import { DEFAULT_CONFIG } from 'components/common/graphicUploader/constants';
import { MAX_FILE_NAME_LENGTH } from 'shared/domain/document/documentLimits';
import { resolveExtensionFromFileName } from 'shared/utils/fileExtensions';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { ImageRotation } from 'shared/dtos/out/document';
import { FileTranslationKeys } from 'intl/keys';
import { Services } from 'shared/domain/messages/message';
import { DocumentationTranslationKeys } from 'intl/keys';
import { useUploadProgressStore } from 'store/uploadProgress';
import { enrichPdfUploadPageCountsBatch } from 'services/fileService/pdfConvertStatusService';
import { UploadConfig } from './types';

export function toFileReadResult(
  resolve: (result: FileReadResult) => void,
  file: File
): void {
  const MAX_FILE_SIZE = DEFAULT_CONFIG.max_file_size;

  if (MAX_FILE_SIZE && MAX_FILE_SIZE < file.size) {
    return resolve({
      resultType: ResultType.error,
      error: 'max_file_size',
    } as FileReadError);
  }

  const url = URL.createObjectURL(file);

  return resolve({
    file,
    resultType: ResultType.success,
    fileStateKind: FILE_STATE_KIND.UNSAVED,
    src: url,
    size: file.size,
    title: file.name.substring(0, MAX_FILE_NAME_LENGTH),
    description: '',
    type: file.type,
    data: {
      extension: resolveExtensionFromFileName(file.name),
      name: file.name,
    },
    downloadSrc: url,
    syncStatus: SyncStatus.PENDING,
    drawingSyncStatus: SyncStatus.PENDING,
    imageRotation: ImageRotation.ROTATE_0,
  } as FileReadSuccess);
}

export function processNewFile(file: File): Promise<FileReadResult> {
  return new Promise((res) => {
    if (!file) {
      const error: FileReadError = {
        resultType: ResultType.error,
        error: 'no_file',
      };
      return res(error);
    }
    toFileReadResult(res, file);
  });
}

export function isFileReadSuccess(
  file: FileReadResult
): file is FileReadSuccess {
  return file.resultType === ResultType.success;
}

export function isFileReadError(file: FileReadResult): file is FileReadError {
  return file.resultType !== ResultType.success;
}

export function showErrors(failedFiles: FileReadError[]): void {
  if (failedFiles.length) {
    if (failedFiles.length > 1) {
      showErrorToastWithMessage(FileTranslationKeys.manyFilesError, {
          amount: failedFiles.length,
        });
    } else {
      showErrorToastWithMessage(FileTranslationKeys.fileUploadErrorFileTooBig);
    }
  }
}

/**
 * Saves the file locally and resolves with its local id, or `null` when it
 * could not be saved: its placeholder is marked failed and the other files of
 * the upload go on (one failure must not abort a batch of hundreds of files).
 */
export async function saveLocally(
  file: FileReadSuccess,
  author: UserWithPersonalData,
  currentDirectory: string,
  uniqueId?: string
): Promise<number | null> {
  try {
    return await documentationClient.create(
      fileToCreateDocumentationModel(file, author, currentDirectory),
      uniqueId
    );
  } catch (error) {
    logError('Error while saving an uploaded documentation file', error);
    if (uniqueId) {
      useUploadProgressStore.getState().markUploadPlaceholderFailed(uniqueId);
    }
    return null;
  }
}

/** The local ids of the saved files; one error toast when some could not be saved. */
export function toSavedLocalIds(localIds: (number | null)[]): number[] {
  const savedLocalIds = localIds.filter((localId): localId is number => localId !== null);
  if (savedLocalIds.length < localIds.length) {
    showGenericErrorToast();
  }
  return savedLocalIds;
}

export function getAuthor({
  currentUser,
  organizationId,
  projectId,
}: UploadConfig): UserWithPersonalData {
  return userWithPermissionsInDtoToUserWithPersonalData(
    currentUser,
    organizationId,
    projectId
  );
}

export function registerUploadPlaceholders(files: File[]): Map<File, string> {
  const { addPendingUploadPlaceholders } = useUploadProgressStore.getState();

  const placeholderIds = addPendingUploadPlaceholders(
    files.map((file) => ({
      title: file.name.substring(0, MAX_FILE_NAME_LENGTH),
      type: Services.DOCUMENTATIONS,
    }))
  );

  return new Map(files.map((file, index) => [file, placeholderIds[index]]));
}

export function registerDirectoryCreationPlaceholder(): string {
  const { addPendingUploadPlaceholders } = useUploadProgressStore.getState();

  const [placeholderId] = addPendingUploadPlaceholders([
    {
      title: 'Creating folders...',
      type: Services.DOCUMENTATIONS,
      titleTranslationKey:
        DocumentationTranslationKeys.folderUploadCreatingFolders,
    },
  ]);

  return placeholderId;
}

export function resolveUniqueId(
  file: FileReadSuccess,
  uniqueIdByFile: Map<File, string>
): string | undefined {
  return file.file ? uniqueIdByFile.get(file.file) : undefined;
}

export async function readFilesWithPlaceholders(
  files: File[],
  uniqueIdByFile: Map<File, string>
): Promise<{
  successfullFiles: FileReadSuccess[];
  failedFiles: FileReadError[];
}> {
  const { markUploadPlaceholderFailed } = useUploadProgressStore.getState();
  const processedFiles = await Promise.all(files.map(processNewFile));

  processedFiles.forEach((result, index) => {
    if (!isFileReadSuccess(result)) {
      const placeholderId = uniqueIdByFile.get(files[index]);
      if (placeholderId) markUploadPlaceholderFailed(placeholderId);
    }
  });

  const successfullFiles = await enrichPdfUploadPageCountsBatch(
    processedFiles.filter(isFileReadSuccess)
  );

  return {
    successfullFiles,
    failedFiles: processedFiles.filter(isFileReadError),
  };
}

export function getDirectoryPath({ file }: FileReadSuccess): string {
  const path = file?.webkitRelativePath;
  const parts = path?.split('/');
  parts?.pop();

  return parts?.join('/') || '';
}

export function sortFilesByFileName(a: File, b: File): number {
  const aName = a.webkitRelativePath?.split('/').pop() || '';
  const bName = b.webkitRelativePath?.split('/').pop() || '';

  const aNum = parseInt(/(\d+)/.exec(aName)?.[1] || '0');
  const bNum = parseInt(/(\d+)/.exec(bName)?.[1] || '0');

  return aNum !== bNum ? aNum - bNum : aName.localeCompare(bName);
}

export function createDirectoriesBatch(
  directories: DirectoriesBatchCreateModel
): Promise<DirectoriesBatchCreatedModel> {
  return directoryClient.createBatch(directories);
}
